/**
 * Navigation trees for both portals. The tree is data, not JSX, so the
 * sidebar component doesn't have to know which portal it's rendering —
 * it just maps. PR-B/C/D fill in the actual route components; the empty
 * portal shells in PR-A render placeholders for the routes listed here.
 */

import type { ComponentType } from 'react';
import {
  AccountTree as AccountTreeIcon,
  Api as ApiIcon,
  Article as ArticleIcon,
  Book as BookIcon,
  Code as CodeIcon,
  Dashboard as DashboardIcon,
  Hub as HubIcon,
  Lock as LockIcon,
  MedicalServices as ConsultationIcon,
  PeopleAlt as UsersIcon,
  PrivacyTip as ConsentIcon,
  QueryStats as MetricsIcon,
  RecordVoiceOver as AbhaIcon,
  Security as SecurityIcon,
  Settings as SettingsIcon,
  Storage as RollupIcon,
  Webhook as WebhookIcon,
  Workspaces as TenantsIcon,
} from '@mui/icons-material';
import type { SvgIconProps } from '@mui/material';
import type { Portal } from '@/auth/AuthContext';

export interface NavItem {
  label: string;
  to: string;
  icon: ComponentType<SvgIconProps>;
  /** Stable id for testing + analytics. */
  id: string;
}

export interface NavSection {
  label: string;
  items: NavItem[];
}

const platformNav: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { id: 'platform-dashboard', label: 'Dashboard', to: '/platform', icon: DashboardIcon },
    ],
  },
  {
    label: 'Tenants',
    items: [
      { id: 'platform-tenants', label: 'Tenants', to: '/platform/tenants', icon: TenantsIcon },
      { id: 'platform-facilities', label: 'Facilities', to: '/platform/facilities', icon: AccountTreeIcon },
      { id: 'platform-security', label: 'Tenant Security', to: '/platform/security', icon: SecurityIcon },
    ],
  },
  {
    label: 'Operations',
    items: [
      { id: 'platform-audit', label: 'Audit Viewer', to: '/platform/audit', icon: ArticleIcon },
      { id: 'platform-webhooks', label: 'Webhooks', to: '/platform/webhooks', icon: WebhookIcon },
      { id: 'platform-rollup', label: 'Metrics Rollup', to: '/platform/rollup', icon: RollupIcon },
      { id: 'platform-secrets', label: 'Secrets', to: '/platform/secrets', icon: LockIcon },
      { id: 'platform-api-masters', label: 'API Masters', to: '/platform/api-masters', icon: ApiIcon },
      { id: 'platform-bridge', label: 'ABDM Bridge', to: '/platform/bridge', icon: HubIcon },
    ],
  },
];

const tenantNav: NavSection[] = [
  {
    label: 'Overview',
    items: [
      { id: 'tenant-dashboard', label: 'Dashboard', to: '/tenant', icon: DashboardIcon },
      { id: 'tenant-metrics', label: 'Metrics', to: '/tenant/metrics', icon: MetricsIcon },
    ],
  },
  {
    label: 'ABDM',
    items: [
      { id: 'tenant-abha', label: 'ABHA', to: '/tenant/abha', icon: AbhaIcon },
      { id: 'tenant-consultations', label: 'Consultations', to: '/tenant/consultations', icon: ConsultationIcon },
      { id: 'tenant-care-context', label: 'Care Context', to: '/tenant/care-context', icon: AccountTreeIcon },
      { id: 'tenant-consent', label: 'Consent', to: '/tenant/consent', icon: ConsentIcon },
    ],
  },
  {
    label: 'Administration',
    items: [
      { id: 'tenant-users', label: 'Users', to: '/tenant/users', icon: UsersIcon },
      { id: 'tenant-settings', label: 'Settings', to: '/tenant/settings', icon: SettingsIcon },
    ],
  },
  {
    label: 'Operations',
    items: [
      { id: 'tenant-audit', label: 'My Audit', to: '/tenant/audit', icon: ArticleIcon },
      { id: 'tenant-webhooks', label: 'Webhooks', to: '/tenant/webhooks', icon: WebhookIcon },
    ],
  },
  {
    label: 'Integration',
    items: [
      { id: 'tenant-int-abha',      label: 'ABHA Creation',   to: '/tenant/integration/abha',         icon: AbhaIcon },
      { id: 'tenant-int-fhir',      label: 'FHIR Bundles',    to: '/tenant/integration/fhir-bundles', icon: AccountTreeIcon },
      { id: 'tenant-int-consent',   label: 'Consent Request', to: '/tenant/integration/consent',      icon: ConsentIcon },
      { id: 'tenant-api-explorer',  label: 'API Explorer',    to: '/tenant/api-explorer',             icon: CodeIcon },
      { id: 'tenant-api-reference', label: 'API Reference',   to: '/tenant/api-reference',            icon: BookIcon },
    ],
  },
];

export function navTreeFor(portal: Portal): NavSection[] {
  return portal === 'platform' ? platformNav : tenantNav;
}
